import { useState, useEffect } from 'react';
import { BottomNav } from '../components/BottomNav';
import { Card, CardContent } from '../components/ui/card';
import { Progress as ProgressBar } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { 
  Flame, Trophy, Calendar, TrendingUp, Check, X, Heart, 
  MessageCircle, HandHeart, Users, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Progress() {
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [progressRes, historyRes] = await Promise.all([
        axios.get(`${API}/progress`),
        axios.get(`${API}/progress/history?days=30`)
      ]);
      setProgress(progressRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      toast.error('Could not load progress');
    } finally {
      setLoading(false);
    }
  };

  // Generate last 7 days
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayHistory = history.find(h => h.date === dateStr);
    return {
      date,
      dateStr,
      dayName: format(date, 'EEE'),
      completed: !!dayHistory?.completed_at,
      stepsCompleted: dayHistory?.completed_steps?.length || 0
    };
  });

  const completionRate = last7Days.filter(d => d.completed).length / 7 * 100;

  if (loading) {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center">
        <div className="animate-gentle-pulse">
          <TrendingUp className="w-12 h-12 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen sky-gradient-light pb-safe" data-testid="progress-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <h1 className="text-2xl font-bold text-gray-800">Your Impact</h1>
        <p className="text-gray-600">See how you&apos;re making a difference</p>
      </header>

      <main className="px-6 space-y-5">
        {/* Main Stats Card */}
        <Card className="rounded-3xl border-0 shadow-card bg-gradient-to-br from-blue-500 to-blue-600 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-blue-100 text-sm font-medium">Current Streak</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-5xl font-bold">
                    {progress?.streak_count || 0}
                  </span>
                  <span className="text-xl text-blue-200">
                    days
                  </span>
                </div>
              </div>
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-4xl">🙏</span>
              </div>
            </div>
            
            {/* Weekly dots */}
            <div className="flex justify-between">
              {last7Days.map((day, idx) => (
                <div key={idx} className="flex flex-col items-center gap-1">
                  <span className="text-xs text-blue-200">{day.dayName}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    day.completed 
                      ? 'bg-white text-blue-500' 
                      : day.stepsCompleted > 0
                        ? 'bg-blue-400 text-white'
                        : 'bg-blue-400/50 text-blue-200'
                  }`}>
                    {day.completed ? (
                      <Check className="w-4 h-4" strokeWidth={3} />
                    ) : day.stepsCompleted > 0 ? (
                      <span className="text-xs font-bold">{day.stepsCompleted}</span>
                    ) : (
                      <X className="w-3 h-3 opacity-50" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="rounded-2xl border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-2">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-2xl font-bold text-gray-800">{progress?.streak_count || 0}</p>
              <p className="text-xs text-gray-500 font-medium">Days</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center mx-auto mb-2">
                <Heart className="w-5 h-5 text-pink-500" />
              </div>
              <p className="text-2xl font-bold text-gray-800">{progress?.total_sessions || 0}</p>
              <p className="text-xs text-gray-500 font-medium">Sessions</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-2">
                <HandHeart className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-800">{progress?.total_sessions || 0}</p>
              <p className="text-xs text-gray-500 font-medium">Good Deeds</p>
            </CardContent>
          </Card>
        </div>

        {/* This Month Stats */}
        <Card className="rounded-2xl border-0 shadow-card bg-white">
          <CardContent className="p-5">
            <h3 className="font-bold text-gray-800 mb-4">This Month</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="font-medium text-gray-700">Prayers Given</span>
                </div>
                <span className="font-bold text-gray-800">{progress?.total_sessions || 0}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                  </div>
                  <span className="font-medium text-gray-700">Verses Read</span>
                </div>
                <span className="font-bold text-gray-800">{progress?.total_sessions || 0}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <HandHeart className="w-5 h-5 text-amber-500" />
                  </div>
                  <span className="font-medium text-gray-700">Kindness Acts</span>
                </div>
                <span className="font-bold text-gray-800">{progress?.total_sessions || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Progress */}
        <Card className="rounded-2xl border-0 shadow-card bg-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800">Weekly Completion</h3>
              <span className="text-sm text-gray-500">
                {last7Days.filter(d => d.completed).length}/7 days
              </span>
            </div>
            <ProgressBar value={completionRate} className="h-3" />
            <p className="text-sm text-gray-500 mt-3">
              {completionRate === 100 
                ? "Perfect week! Keep praying! 🙏" 
                : completionRate >= 70 
                  ? "Great progress! Keep going! 💪"
                  : completionRate >= 40
                    ? "You're building momentum! 🌱"
                    : "Every day is a new beginning 🌅"
              }
            </p>
          </CardContent>
        </Card>

        {/* Encouragement */}
        <Card className="rounded-2xl border-0 shadow-card bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="p-5 text-center">
            <p className="font-medium text-green-800 text-lg mb-1">
              &ldquo;Faithful in little, faithful in much&rdquo;
            </p>
            <p className="text-sm text-green-600">
              Every act of kindness creates ripples of change
            </p>
          </CardContent>
        </Card>
      </main>

      <BottomNav />
    </div>
  );
}
